import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, GripVertical, Globe, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface QuestionManagementProps {
  companyId: string;
  firmId: string;
  category: string;
  categoryName: string;
  accessToken: string;
  onClose: () => void;
}

interface Question {
  id: string;
  questionText: string;
  questionType: 'text' | 'textarea' | 'boolean' | 'multiple_choice' | 'checkbox' | 'select';
  options?: string[];
  placeholder?: string;
  helpText?: string;
  customQuestion?: boolean; // Added by consultant
  order?: number;
}

export function QuestionManagement({
  companyId,
  firmId,
  category,
  categoryName,
  accessToken,
  onClose,
}: QuestionManagementProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [globalQuestions, setGlobalQuestions] = useState<Question[]>([]);
  const [enabledGlobalIds, setEnabledGlobalIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'custom' | 'global'>('custom');
  const [newQuestion, setNewQuestion] = useState<Partial<Question>>({
    questionType: 'text',
    options: [],
  });

  useEffect(() => {
    loadQuestions();
  }, [category]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      // Load default questions
      const defaultQuestionsResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/guided-questions/${category}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!defaultQuestionsResponse.ok) {
        throw new Error('Failed to load questions');
      }

      const defaultQuestions = await defaultQuestionsResponse.json();

      // Load custom questions for this firm
      const customQuestionsResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/custom-questions/${category}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      let customQuestions = [];
      if (customQuestionsResponse.ok) {
        customQuestions = await customQuestionsResponse.json();
      }

      // Merge and sort
      const allQuestions = [
        ...defaultQuestions.map((q: Question, i: number) => ({ ...q, order: i })),
        ...customQuestions.map((q: Question) => ({ ...q, customQuestion: true })),
      ].sort((a, b) => (a.order || 999) - (b.order || 999));

      setQuestions(allQuestions);
    } catch (error) {
      console.error('[QUESTION_MGMT] Error loading:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.questionText || !newQuestion.questionType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/custom-questions/${category}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...newQuestion,
            id: `custom_${Date.now()}`,
            customQuestion: true,
            order: questions.length,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add question');
      }

      toast.success('Question added successfully');
      setShowAddDialog(false);
      setNewQuestion({ questionType: 'text', options: [] });
      await loadQuestions();
    } catch (error) {
      console.error('[QUESTION_MGMT] Error adding question:', error);
      toast.error('Failed to add question');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuestion = async (question: Question) => {
    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/custom-questions/${category}/${question.id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(question),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update question');
      }

      toast.success('Question updated successfully');
      setEditingQuestion(null);
      await loadQuestions();
    } catch (error) {
      console.error('[QUESTION_MGMT] Error updating question:', error);
      toast.error('Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/custom-questions/${category}/${questionId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete question');
      }

      toast.success('Question deleted successfully');
      await loadQuestions();
    } catch (error) {
      console.error('[QUESTION_MGMT] Error deleting question:', error);
      toast.error('Failed to delete question');
    } finally {
      setSaving(false);
    }
  };

  const handleReorderQuestions = async (fromIndex: number, toIndex: number) => {
    const reordered = [...questions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const updated = reordered.map((q, i) => ({ ...q, order: i }));
    setQuestions(updated);

    // Save new order
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/custom-questions/${category}/reorder`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questions: updated.map((q) => ({ id: q.id, order: q.order })),
          }),
        }
      );
    } catch (error) {
      console.error('[QUESTION_MGMT] Error reordering:', error);
      toast.error('Failed to save new order');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-gray-900">Manage Questions - {categoryName}</h2>
          <p className="text-gray-600 text-sm mt-1">
            Customize the discovery questions for this category
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-3">
        {questions.map((question, index) => (
          <Card key={question.id} className={question.customQuestion ? 'border-purple-200 bg-purple-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-2 text-gray-400">
                  <GripVertical className="w-4 h-4 cursor-move" />
                  <span className="text-sm">{index + 1}</span>
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{question.questionText}</p>
                        {question.customQuestion && (
                          <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                            Custom
                          </Badge>
                        )}
                      </div>
                      {question.helpText && (
                        <p className="text-sm text-gray-600 mb-2">{question.helpText}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <Badge variant="secondary">{question.questionType}</Badge>
                        {question.options && question.options.length > 0 && (
                          <span>{question.options.length} options</span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {question.customQuestion && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingQuestion(question)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Question Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Custom Question</DialogTitle>
            <DialogDescription>
              Create a new question for the {categoryName} discovery process
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Question Text *</Label>
              <Input
                value={newQuestion.questionText || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, questionText: e.target.value })}
                placeholder="e.g., How many users will need access?"
              />
            </div>

            <div className="space-y-2">
              <Label>Question Type *</Label>
              <Select
                value={newQuestion.questionType}
                onValueChange={(value: any) => setNewQuestion({ ...newQuestion, questionType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Short Text</SelectItem>
                  <SelectItem value="textarea">Long Text</SelectItem>
                  <SelectItem value="boolean">Yes/No</SelectItem>
                  <SelectItem value="multiple_choice">Multiple Choice (single)</SelectItem>
                  <SelectItem value="checkbox">Checkboxes (multiple)</SelectItem>
                  <SelectItem value="select">Dropdown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {['multiple_choice', 'checkbox', 'select'].includes(newQuestion.questionType || '') && (
              <div className="space-y-2">
                <Label>Options (one per line)</Label>
                <Textarea
                  rows={4}
                  value={newQuestion.options?.join('\n') || ''}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      options: e.target.value.split('\n').filter((o) => o.trim()),
                    })
                  }
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Help Text</Label>
              <Textarea
                rows={2}
                value={newQuestion.helpText || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, helpText: e.target.value })}
                placeholder="Additional context or explanation for this question"
              />
            </div>

            <div className="space-y-2">
              <Label>Placeholder (optional)</Label>
              <Input
                value={newQuestion.placeholder || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, placeholder: e.target.value })}
                placeholder="Placeholder text for text inputs"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddQuestion}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Adding...' : 'Add Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={!!editingQuestion} onOpenChange={(open) => !open && setEditingQuestion(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
            <DialogDescription>Update the question details</DialogDescription>
          </DialogHeader>

          {editingQuestion && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Question Text *</Label>
                <Input
                  value={editingQuestion.questionText}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, questionText: e.target.value })
                  }
                />
              </div>

              {['multiple_choice', 'checkbox', 'select'].includes(editingQuestion.questionType) && (
                <div className="space-y-2">
                  <Label>Options (one per line)</Label>
                  <Textarea
                    rows={4}
                    value={editingQuestion.options?.join('\n') || ''}
                    onChange={(e) =>
                      setEditingQuestion({
                        ...editingQuestion,
                        options: e.target.value.split('\n').filter((o) => o.trim()),
                      })
                    }
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Help Text</Label>
                <Textarea
                  rows={2}
                  value={editingQuestion.helpText || ''}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, helpText: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingQuestion(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => editingQuestion && handleUpdateQuestion(editingQuestion)}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}